/**
 * API client for activity creation
 */

/**
 * Fetch available activity templates for a child
 * @param {string|number} childId - The child ID
 * @param {string} csrfToken - CSRF token for authentication
 * @returns {Promise<Array>} Array of activity templates
 */
export async function fetchActivityTemplates(childId, csrfToken) {
  const response = await fetch(`/api/children/${childId}/activity_templates`, {
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch activity templates: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Validate credentials for an external service
 * @param {string|number} childId - The child ID
 * @param {string} activityType - The activity type (e.g., 'duolingo', 'khan_academy')
 * @param {Object} credentials - Username and/or password
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Validation result with success status
 */
export async function validateCredentials(childId, activityType, credentials, csrfToken) {
  const response = await fetch(`/api/children/${childId}/validate_credentials`, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      activity_type: activityType,
      ...credentials
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Validation failed');
  }

  return response.json();
}

/**
 * Fetch available courses for an activity type
 * @param {string|number} childId - The child ID
 * @param {string} activityType - The activity type
 * @param {Object} credentials - Username and/or password
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Course structure data
 */
export async function fetchCourses(childId, activityType, credentials, csrfToken) {
  const response = await fetch(`/api/children/${childId}/courses`, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      activity_type: activityType,
      ...credentials
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch courses');
  }

  return response.json();
}

/**
 * Check if a child has a service account for a given service
 * @param {string|number} childId - The child ID
 * @param {string} service - The service name (duolingo, khan_academy, codecombat)
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Service account status
 */
export async function getServiceAccount(childId, service, csrfToken) {
  const response = await fetch(`/api/children/${childId}/service_accounts/${service}`, {
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check service account');
  }

  return response.json();
}

/**
 * Preview a preloaded account without creating a service account
 * Used during activity creation to allow switching before final submission
 * @param {string|number} childId - The child ID
 * @param {string} service - The service name (duolingo, khan_academy, codecombat)
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Preloaded account details for validation
 */
export async function previewPreloadedAccount(childId, service, csrfToken) {
  const response = await fetch(`/api/children/${childId}/service_accounts/${service}/preview`, {
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to preview preloaded account');
  }

  return response.json();
}

/**
 * Update an existing service account for a child
 * @param {string|number} childId - The child ID
 * @param {string} service - The service name
 * @param {boolean} usePreloaded - Whether to use a preloaded account
 * @param {Object} details - Account details (username, password) if not using preloaded
 * @param {string} csrfToken - CSRF token
 * @param {Array} flags - Array of flags (e.g., ['super']) (optional)
 * @returns {Promise<Object>} Updated service account
 */
export async function updateServiceAccount(childId, service, usePreloaded, details, csrfToken, flags = null) {
  const body = {
    use_preloaded: usePreloaded
  };

  if (!usePreloaded && details) {
    body.details = details;
  }

  if (flags !== null) {
    body.flags = flags;
  }

  const response = await fetch(`/api/children/${childId}/service_accounts/${service}`, {
    method: 'PUT',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update service account');
  }

  return response.json();
}

/**
 * Create a service account for a child
 * @param {string|number} childId - The child ID
 * @param {string} service - The service name
 * @param {boolean} usePreloaded - Whether to use a preloaded account
 * @param {Object} details - Account details (username, password) if not using preloaded
 * @param {string} csrfToken - CSRF token
 * @param {Array} flags - Array of flags (e.g., ['super']) (optional, defaults to empty)
 * @returns {Promise<Object>} Created service account
 */
export async function createServiceAccount(childId, service, usePreloaded, details, csrfToken, flags = []) {
  const body = {
    service,
    use_preloaded: usePreloaded,
    flags
  };

  if (!usePreloaded && details) {
    body.details = details;
  }

  const response = await fetch(`/api/children/${childId}/service_accounts`, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create service account');
  }

  return response.json();
}

/**
 * Poll an external service query until it completes
 * @param {string} queryId - The query ID
 * @param {string} csrfToken - CSRF token
 * @param {number} maxAttempts - Maximum polling attempts (default 60)
 * @param {number} intervalMs - Polling interval in ms (default 1000)
 * @returns {Promise<Object>} Query result
 */
async function pollExternalServiceQuery(queryId, csrfToken, maxAttempts = 60, intervalMs = 1000) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await fetch(`/api/services/${queryId}`, {
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to check query status');
    }

    const data = await response.json();

    if (data.status === 'successful') {
      return data.outputs;
    } else if (data.status === 'error') {
      throw new Error(data.outputs?.error || 'Query failed');
    }

    // Still pending/processing, wait and retry
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Query timed out');
}

/**
 * Send OTP to email for ArtWorkout account verification
 * Uses ExternalServiceQuery for async processing and mocking support
 * @param {string|number} childId - The child ID
 * @param {string} email - User's email address
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Result with success status and message
 */
export async function sendArtWorkoutOtp(childId, email, csrfToken) {
  // Create query via ExternalServiceQuery
  const queryParams = new URLSearchParams({
    name: 'art_workout',
    action: 'send_otp',
    'details[email]': email
  });

  const response = await fetch(`/api/services?${queryParams.toString()}`, {
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json'
    },
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send OTP');
  }

  // Poll for completion
  return pollExternalServiceQuery(data.query_id, csrfToken);
}

/**
 * Verify OTP and get credentials for ArtWorkout
 * Uses ExternalServiceQuery's validate_credentials action
 * Credentials are then saved through the regular service account flow
 * @param {string|number} childId - The child ID
 * @param {string} email - User's email address
 * @param {string} otp - OTP code from email
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Credentials (email, access_token, refresh_token, user_id)
 */
export async function verifyArtWorkoutOtp(childId, email, otp, csrfToken) {
  // Create query via ExternalServiceQuery
  const queryParams = new URLSearchParams({
    name: 'art_workout',
    action: 'validate_credentials',
    'details[email]': email,
    'details[otp]': otp
  });

  const response = await fetch(`/api/services?${queryParams.toString()}`, {
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json'
    },
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Invalid OTP');
  }

  // Poll for completion - result contains credentials
  return pollExternalServiceQuery(data.query_id, csrfToken);
}

/**
 * Create a new curriculum activity
 * @param {string|number} childId - The child ID
 * @param {Object} activityData - Activity configuration data
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Created activity
 */
export async function createActivity(childId, activityData, csrfToken) {
  const response = await fetch(`/api/children/${childId}/curriculum/activities`, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      curriculum_activity: activityData
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create activity');
  }

  return response.json();
}
